import type { Vault } from '@/types/vault'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { type Address, zeroAddress } from 'viem'

export const transactionsContextQueryKey = ['transactionsContext'] as const

interface TransactionsContextQueryParams {
  address: Address | undefined
  vault: Vault
  initialData?: Awaited<ReturnType<ReturnType<typeof getDefiSdk>['getTransactionsContext']>>
  initialDataAddress?: Address
}

function transactionsContextQueryOptions({
  address = zeroAddress,
  vault,
  initialData,
  initialDataAddress,
}: TransactionsContextQueryParams) {
  return queryOptions({
    queryKey: [
      ...transactionsContextQueryKey,
      {
        userAddress: address.toLowerCase() as Address,
        network: vault.network.name,
        vaultId: vault.vaultId,
      },
    ] as const,
    async queryFn({ queryKey }) {
      const { network, vaultId, userAddress } = queryKey[1]
      const defiSdk = getDefiSdk()
      return await defiSdk.getTransactionsContext({
        path: { userAddress, network, vaultId },
      })
    },
    select(data) {
      return {
        ...data,
        allSteps: [
          data.depositSteps,
          data.redeemSteps,
          data.rewards?.claimable?.length ? data.rewards.steps : [],
        ].flat(),
      }
    },
    initialData: initialDataAddress?.toLowerCase() === address.toLowerCase() ? initialData : undefined,
    placeholderData: undefined,
    enabled: vault.isTransactional,
    retry: 1,
    staleTime: 10 * SECOND_IN_MILLISECONDS,
    refetchInterval: 10 * SECOND_IN_MILLISECONDS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useTransactionsContextQuery(params: TransactionsContextQueryParams) {
  return useQuery(transactionsContextQueryOptions(params))
}

export type TransactionsContext = ReturnType<typeof useTransactionsContextQuery>['data']
